import clsx from 'clsx';
import {
  AlertTriangle,
  Camera,
  Check,
  CircleSlash,
  MessageSquare,
  Tag,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CILT_LABELS,
  describeReading,
  describeSnapshot,
  numericBounds,
  type CiltType,
  type RunnableItem,
} from '@odr/shared';

import {
  Badge,
  Button,
  QualityWarning,
  SafetyWarning,
  StatusPill,
  Textarea,
} from '../../components/ui';
import type { LocalAnswer } from './useRun';

const CILT_TONE: Record<string, string> = {
  C: 'bg-info-100 text-info-700',
  I: 'bg-steel-200 text-steel-700',
  L: 'bg-warn-100 text-warn-700',
  T: 'bg-ok-100 text-ok-700',
  OTHER: 'bg-steel-100 text-steel-600',
};

export interface ItemCardProps {
  item: RunnableItem;
  answer: LocalAnswer | undefined;
  outstanding: string[];
  tagNo: string | undefined;
  evidenceCount: number;
  readOnly?: boolean;
  onAnswer: (value: {
    valueText?: string | null;
    valueNumber?: number | null;
    valueBool?: boolean | null;
    valueList?: string[] | null;
    markedNotApplicable?: boolean;
    comment?: string | null;
  }) => Promise<{ error?: string } | undefined | { evaluation: { reason: string | null } }>;
  onPhoto: (file: File) => void;
  onRaiseProblem: () => void;
}

/**
 * One checklist task, sized for a phone in a gloved hand.
 *
 * Layout order is deliberate: safety warning first (before the operator can act
 * on the task), then the standard they are judging against, then the control.
 */
export function ItemCard({
  item,
  answer,
  outstanding,
  tagNo,
  evidenceCount,
  readOnly,
  onAnswer,
  onPhoto,
  onRaiseProblem,
}: ItemCardProps) {
  const [comment, setComment] = useState(answer?.comment ?? '');
  const [localError, setLocalError] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const isAbnormal = answer?.result === 'FAIL' || answer?.result === 'ABNORMAL';
  const isAnswered = Boolean(answer);
  const needsAttention = outstanding.length > 0;

  const submitValue = async (value: Parameters<ItemCardProps['onAnswer']>[0]) => {
    setLocalError(null);
    const result = await onAnswer({ ...value, comment: comment || null });
    if (result && 'error' in result && result.error) {
      setLocalError(result.error);
      setReason(null);
    } else if (result && 'evaluation' in result) {
      setReason(result.evaluation.reason);
    }
  };

  const saveComment = async (text: string) => {
    setComment(text);
    if (!answer) return;
    await onAnswer({
      valueText: answer.valueText,
      valueNumber: answer.valueNumber,
      valueBool: answer.valueBool,
      valueList: answer.valueList,
      comment: text || null,
    });
  };

  return (
    <article
      id={`item-${item.id}`}
      className={clsx(
        'scroll-mt-32 border-b-8 border-steel-100 bg-white px-4 py-4 last:border-b-0',
        needsAttention && 'ring-2 ring-alarm-500 ring-inset',
      )}
    >
      <header className="flex items-start gap-3">
        <span
          className={clsx(
            'grid size-8 shrink-0 place-items-center rounded-lg text-sm font-bold',
            isAnswered && !isAbnormal && 'bg-ok-100 text-ok-700',
            isAbnormal && 'bg-alarm-100 text-alarm-700',
            !isAnswered && 'bg-steel-100 text-steel-600',
          )}
        >
          {isAnswered && !isAbnormal ? (
            <Check aria-hidden className="size-4" />
          ) : isAbnormal ? (
            <AlertTriangle aria-hidden className="size-4" />
          ) : (
            item.seq
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={clsx(
                'rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase',
                CILT_TONE[item.ciltType] ?? CILT_TONE.OTHER,
              )}
            >
              {CILT_LABELS[item.ciltType as CiltType] ?? item.ciltType}
            </span>
            {item.componentName ? (
              <span className="text-[11px] text-steel-500">{item.componentName}</span>
            ) : null}
            {!item.isRequired ? <Badge>Optional</Badge> : null}
          </div>

          <h3 className="mt-1 text-base leading-snug font-bold text-steel-900">
            <span className="text-steel-400">#{item.seq}</span> {item.task}
          </h3>

          {item.standard ? (
            <p className="mt-1 text-sm text-steel-600">
              <span className="font-semibold text-steel-500">Standard: </span>
              {item.standard}
            </p>
          ) : null}
          {item.toolMethod ? (
            <p className="text-xs text-steel-500">Method: {item.toolMethod}</p>
          ) : null}
        </div>
      </header>

      {item.safetyWarning ? (
        <div className="mt-3">
          <SafetyWarning>{item.safetyWarning}</SafetyWarning>
        </div>
      ) : null}
      {item.qualityWarning ? (
        <div className="mt-2">
          <QualityWarning>{item.qualityWarning}</QualityWarning>
        </div>
      ) : null}

      {item.instruction ? (
        <p className="mt-3 rounded-lg bg-steel-50 p-3 text-sm whitespace-pre-line text-steel-700">
          {item.instruction}
        </p>
      ) : null}

      {item.oplId ? (
        <Link
          to={`/opl/${item.oplId}`}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-info-700 hover:underline"
        >
          📘 Read the One-Point Lesson{item.oplTitle ? `: ${item.oplTitle}` : ''}
        </Link>
      ) : null}

      {/* --- Answer control --- */}
      <div className="mt-4">
        <AnswerControl
          item={item}
          answer={answer}
          readOnly={readOnly}
          onSubmit={submitValue}
        />
      </div>

      {localError ? (
        <p role="alert" className="mt-2 text-sm font-semibold text-alarm-600">
          {localError}
        </p>
      ) : null}

      {isAbnormal ? (
        <div className="mt-3 rounded-lg border-2 border-alarm-500 bg-alarm-50 p-3">
          <p className="flex items-center gap-1.5 text-sm font-bold text-alarm-700">
            <AlertTriangle aria-hidden className="size-4" />
            Abnormal condition
          </p>
          {reason ?? answerReason(item, answer) ? (
            <p className="mt-1 text-sm text-alarm-700">{reason ?? answerReason(item, answer)}</p>
          ) : null}
        </div>
      ) : null}

      {/* --- Follow-up --- */}
      {(isAbnormal || item.requiresPhoto || answer?.comment) && !readOnly ? (
        <div className="mt-3 space-y-3">
          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onBlur={(event) => void saveComment(event.target.value)}
            placeholder="What did you find? (a short note is enough)"
            aria-label={`Note for task ${item.seq}`}
            rows={2}
          />

          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onPhoto(file);
                event.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="secondary"
              icon={Camera}
              onClick={() => fileRef.current?.click()}
            >
              {evidenceCount > 0 ? `Photo added (${evidenceCount})` : 'Take photo'}
            </Button>

            {tagNo ? (
              <StatusPill
                tone="warn"
                label={tagNo === 'pending' ? 'Problem tag queued' : `Problem ${tagNo}`}
              />
            ) : (
              <Button type="button" variant="danger" icon={Tag} onClick={onRaiseProblem}>
                Report problem
              </Button>
            )}
          </div>
        </div>
      ) : null}

      {needsAttention ? (
        <ul className="mt-3 space-y-1 rounded-lg bg-warn-50 p-3 text-sm text-warn-700">
          {outstanding.map((requirement) => (
            <li key={requirement} className="flex items-start gap-1.5">
              <span aria-hidden>•</span>
              <span className="font-medium">{requirement}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function answerReason(item: RunnableItem, answer: LocalAnswer | undefined): string | null {
  if (!answer) return null;
  if (answer.valueNumber != null) {
    const { min, max } = numericBounds(item);
    if (min != null && answer.valueNumber < min) {
      return `${answer.valueNumber}${item.unit ? ` ${item.unit}` : ''} is below the minimum of ${min}${item.unit ? ` ${item.unit}` : ''}.`;
    }
    if (max != null && answer.valueNumber > max) {
      return `${answer.valueNumber}${item.unit ? ` ${item.unit}` : ''} is above the maximum of ${max}${item.unit ? ` ${item.unit}` : ''}.`;
    }
  }
  if (answer.valueText) {
    const option = item.options.find((o) => o.value === answer.valueText);
    if (option) return `Operator selected "${option.label}".`;
  }
  if (answer.valueBool === false) return 'Marked FAIL by the operator.';
  return null;
}

// ---------------------------------------------------------------------------
// Per-type controls
// ---------------------------------------------------------------------------

function AnswerControl({
  item,
  answer,
  readOnly,
  onSubmit,
}: {
  item: RunnableItem;
  answer: LocalAnswer | undefined;
  readOnly?: boolean;
  onSubmit: (value: Parameters<ItemCardProps['onAnswer']>[0]) => void | Promise<void>;
}) {
  const disabled = readOnly;

  switch (item.itemType) {
    case 'PASS_FAIL':
      return (
        <BinaryControl
          disabled={disabled}
          value={answer?.valueBool ?? null}
          isNa={answer?.result === 'NA'}
          allowNa={!item.isSafetyCritical}
          passLabel="PASS"
          failLabel="FAIL"
          onChange={(value) => onSubmit({ valueBool: value })}
          onNa={() => onSubmit({ markedNotApplicable: true })}
        />
      );

    case 'YES_NO':
      return (
        <BinaryControl
          disabled={disabled}
          value={answer?.valueBool ?? null}
          isNa={answer?.result === 'NA'}
          allowNa={!item.isSafetyCritical}
          passLabel="YES"
          failLabel="NO"
          onChange={(value) => onSubmit({ valueBool: value })}
          onNa={() => onSubmit({ markedNotApplicable: true })}
        />
      );

    case 'CONFIRMATION':
      return (
        <BinaryControl
          disabled={disabled}
          value={answer?.valueBool ?? null}
          isNa={false}
          allowNa={false}
          passLabel="DONE"
          failLabel="NOT DONE"
          onChange={(value) => onSubmit({ valueBool: value })}
          onNa={() => undefined}
        />
      );

    case 'NUMERIC':
    case 'COUNTER':
      return (
        <NumericControl
          item={item}
          disabled={disabled}
          value={answer?.valueNumber ?? null}
          onChange={(value) => onSubmit({ valueNumber: value })}
        />
      );

    case 'DROPDOWN':
      return (
        <div className="grid gap-2">
          {item.options.map((option) => {
            const selected = answer?.valueText === option.value;
            const bad = option.result === 'ABNORMAL' || option.result === 'FAIL';
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                aria-pressed={selected}
                onClick={() => onSubmit({ valueText: option.value })}
                className={clsx(
                  'answer-button justify-start',
                  selected && bad && 'border-alarm-600 bg-alarm-600 text-white',
                  selected && !bad && 'border-ok-600 bg-ok-600 text-white',
                  !selected && 'border-steel-300 bg-white text-steel-800 hover:bg-steel-50',
                  disabled && 'opacity-60',
                )}
              >
                {selected ? <Check aria-hidden className="size-5" /> : null}
                {option.label}
              </button>
            );
          })}
        </div>
      );

    case 'MULTI_SELECT': {
      const selected = new Set(answer?.valueList ?? []);
      return (
        <div className="grid gap-2">
          {item.options.map((option) => {
            const isOn = selected.has(option.value);
            const bad = option.result === 'ABNORMAL' || option.result === 'FAIL';
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                aria-pressed={isOn}
                onClick={() => {
                  const next = new Set(selected);
                  if (isOn) next.delete(option.value);
                  else next.add(option.value);
                  void onSubmit({ valueList: [...next] });
                }}
                className={clsx(
                  'answer-button justify-start',
                  isOn && bad && 'border-alarm-600 bg-alarm-600 text-white',
                  isOn && !bad && 'border-ok-600 bg-ok-600 text-white',
                  !isOn && 'border-steel-300 bg-white text-steel-800',
                )}
              >
                <span
                  aria-hidden
                  className={clsx(
                    'grid size-5 place-items-center rounded border-2',
                    isOn ? 'border-white' : 'border-steel-400',
                  )}
                >
                  {isOn ? <Check className="size-3.5" /> : null}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    case 'TEXT':
    case 'SIGNATURE':
      return (
        <Textarea
          disabled={disabled}
          defaultValue={answer?.valueText ?? ''}
          onBlur={(event) => onSubmit({ valueText: event.target.value })}
          placeholder={item.itemType === 'SIGNATURE' ? 'Type your full name' : 'Type your answer'}
          aria-label={`Answer for task ${item.seq}`}
          rows={item.itemType === 'SIGNATURE' ? 1 : 3}
        />
      );

    case 'PHOTO':
      return (
        <p className="rounded-lg bg-steel-50 p-3 text-sm text-steel-600">
          Use “Take photo” below to complete this task.
        </p>
      );

    default:
      return (
        <p className="rounded-lg bg-warn-50 p-3 text-sm text-warn-700">
          This task type ({item.itemType}) is not supported by this version of the app.
          Please update ODR on this device.
        </p>
      );
  }
}

function BinaryControl({
  value,
  isNa,
  allowNa,
  passLabel,
  failLabel,
  disabled,
  onChange,
  onNa,
}: {
  value: boolean | null;
  isNa: boolean;
  allowNa: boolean;
  passLabel: string;
  failLabel: string;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  onNa: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled}
          aria-pressed={value === true}
          onClick={() => onChange(true)}
          className={clsx(
            'answer-button',
            value === true
              ? 'border-ok-600 bg-ok-600 text-white'
              : 'border-steel-300 bg-white text-steel-700 hover:bg-ok-50',
          )}
        >
          <Check aria-hidden className="size-5" />
          {passLabel}
        </button>
        <button
          type="button"
          disabled={disabled}
          aria-pressed={value === false}
          onClick={() => onChange(false)}
          className={clsx(
            'answer-button',
            value === false
              ? 'border-alarm-600 bg-alarm-600 text-white'
              : 'border-steel-300 bg-white text-steel-700 hover:bg-alarm-50',
          )}
        >
          <X aria-hidden className="size-5" />
          {failLabel}
        </button>
      </div>
      {allowNa ? (
        <button
          type="button"
          disabled={disabled}
          aria-pressed={isNa}
          onClick={onNa}
          className={clsx(
            'flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border-2 px-4 text-sm font-semibold',
            isNa
              ? 'border-steel-500 bg-steel-500 text-white'
              : 'border-steel-200 bg-white text-steel-500',
          )}
        >
          <CircleSlash aria-hidden className="size-4" />
          Not applicable
        </button>
      ) : null}
    </div>
  );
}

/**
 * Numeric entry.
 *
 * The badge under the field is the honest part: a record-only reading says
 * "Recorded — Not Scored" and never claims the machine is in or out of
 * specification, because no approved acceptance tolerance exists for it.
 */
function NumericControl({
  item,
  value,
  disabled,
  onChange,
}: {
  item: RunnableItem;
  value: number | null;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(value === null ? '' : String(value));
  const { min, max } = numericBounds(item);
  const standardLabel = describeSnapshot(item.standardSnapshot);

  const commit = () => {
    const parsed = Number(text.replace(',', '.'));
    if (text.trim() === '' || Number.isNaN(parsed)) return;
    onChange(parsed);
  };

  const verdict = describeReading(
    {
      minValue: item.minValue,
      maxValue: item.maxValue,
      targetValue: item.targetValue,
      tolerance: item.tolerance,
      unit: item.unit,
      recordOnly: item.recordOnly,
      mode: item.measurementMode,
      explanation: item.measurementNote,
      appliedStandard: null,
    },
    value,
  );

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          // inputMode decimal brings up the number pad without the strict
          // validation quirks of type="number" on mobile browsers.
          inputMode="decimal"
          type="text"
          disabled={disabled}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
              (event.target as HTMLInputElement).blur();
            }
          }}
          aria-label={`Reading for task ${item.seq}${item.unit ? ` in ${item.unit}` : ''}`}
          placeholder="0"
          className="min-h-touch w-full rounded-lg border-2 border-steel-300 px-4 text-center text-3xl font-bold tabular-nums focus:border-info-500"
        />
        {item.unit ? (
          <span className="shrink-0 text-lg font-bold text-steel-500">{item.unit}</span>
        ) : null}
      </div>

      <p className="mt-1.5 text-center text-xs text-steel-500">
        {item.recordOnly ? (
          item.targetValue != null ? (
            <>
              Standard: {item.targetValue}
              {item.unit ? ` ${item.unit}` : ''}
            </>
          ) : (
            'Record the reading.'
          )
        ) : min != null && max != null ? (
          <>
            Standard: {min} to {max}
            {item.unit ? ` ${item.unit}` : ''}
          </>
        ) : min != null ? (
          <>
            Minimum {min}
            {item.unit ? ` ${item.unit}` : ''}
          </>
        ) : max != null ? (
          <>
            Maximum {max}
            {item.unit ? ` ${item.unit}` : ''}
          </>
        ) : (
          'Record the reading.'
        )}
      </p>

      {verdict.label ? (
        <p
          className={clsx(
            'mt-2 rounded-lg px-3 py-2 text-center text-sm font-bold',
            verdict.tone === 'ok' && 'bg-ok-50 text-ok-700',
            verdict.tone === 'alarm' && 'bg-alarm-50 text-alarm-700',
            verdict.tone === 'info' && 'bg-info-50 text-info-700',
          )}
        >
          {verdict.tone === 'ok' ? '✓ ' : verdict.tone === 'alarm' ? '⚠ ' : 'ℹ '}
          {verdict.label.toUpperCase()}
        </p>
      ) : null}

      {item.recordOnly ? (
        <p className="mt-1.5 rounded-lg bg-steel-50 px-3 py-2 text-center text-xs text-steel-600">
          {item.measurementNote ||
            'Engineering acceptance tolerance has not been configured, so this reading is not scored.'}
        </p>
      ) : standardLabel ? (
        // The revision this run was started against, not the current one — a
        // later revision must not appear to have judged an old reading.
        <p className="mt-1.5 text-center text-xs text-steel-500">
          Scored against {standardLabel}
          {item.standardSnapshot?.revisionLabel ? ` · ${item.standardSnapshot.revisionLabel}` : ''}
        </p>
      ) : null}
    </div>
  );
}

export { MessageSquare };
